import {Component, Injector, Input, OnInit} from '@angular/core';
import {ChartComponent} from '../chart.component';
import {ChartType} from '../../../enums/chart-type.enum';
import {NGSIResult} from '../../../models/ngsi/ngsi-result';
import {ColorUtil} from '../../../utils/color-util';
import {DataFormatUtil} from '../../../utils/data-format-util';

@Component({
  selector: 'uruk-pie-chart',
  templateUrl: '../chart.component.html',
  styleUrls: ['../chart.component.scss']
})
export class PieChartComponent extends ChartComponent implements OnInit {

  // Type of the chart: either pie or doughnut. Default is 'pie'
  @Input() type: 'pie' | 'doughnut' = 'pie';

  // Radius of the pie/doughnut chart
  @Input() radius: string | string[];

  // Determines whether to display the labels around the pie chart
  @Input() showLabels: boolean;

  // Indicates whether to show legend or not
  @Input() showGraphic: boolean;

  // Indicates whether to show legend or not
  @Input() showLegend: boolean;

  // responsiveness parameters
  baseFontSize = 9;
  baseDimension = 180;
  private fontScaleFactor = 20; // a scale factor to determine the responsive font size depending on the dimension
  private doughnutInnerRadiusScaleFactor = 1.75; // a scale factor to determine the responsive inner radius of doughnut charts
  private tooltipFontScaleFactor = 1.25; // a scale factor to determine the responsive font size for tooltips
  private total: number = 0;

  constructor(injector: Injector) {
    super(injector);
  }

  ngOnInit() {
    super.ngOnInit();
  }

  /**
   * Returns the default options that won't change based on the chart container dimensions
   */
  defaultChartOptions() {
    const defaultOptions: any = {
      color: this.colors || ColorUtil.colorPalette,
      series: [{
        type: 'pie',
        name: this.seriesName || this.defaultSeriesName,
        label: {
          formatter: this.labelFormatter.bind(this),
          // formatter: '{b}\n({c})',
          align: 'center',
          color: '#FFFFFF'
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        },
        tooltip: {
          trigger: 'item',
          formatter: this.tooltipFormatter.bind(this)
        },
      }]
    };

    // set the data if the chart is initialized with some data
    if (this.data) {

      this.calculateTotal(this.data);
      defaultOptions.series[0].data = this.data;
    }

    return defaultOptions;
  }

  /**
   * Returns the chart options with responsive parameters
   */
  responsiveChartOptions() {
    this.calculateTotal(this.data);
    const viewportMin = Math.min(this.echartsContainer.nativeElement.offsetWidth, this.echartsContainer.nativeElement.offsetHeight);
    const responsiveShowLegend = (this.echartsContainer.nativeElement.clientWidth / 2) > 150;
    const responsiveFontSize = this.baseFontSize + (viewportMin - this.baseDimension) / this.fontScaleFactor;
    const responsiveLabelVisibility = this.showLabels === true || this.showLabels === undefined ? viewportMin < this.baseDimension ? false : true : false;
    const responsiveGraphicLeftPosition = (responsiveShowLegend === false || this.showLegend === false) ? 'center' : '20%';

    let responsiveRadius;
    if (viewportMin < this.baseDimension) {
      responsiveRadius = this.type === ChartType.Doughnut ? ['40%', '80%'] : '80%';
      if (this.showLabels === false) {
        responsiveRadius = this.type === ChartType.Doughnut ? ['60%', '80%'] : '80%';
      }
    } else {/*if (this.baseDimension <= viewportMin && viewportMin < 195) {
      responsiveRadius = 20;
    } else*/
      const radius = 0.0956 * viewportMin + 24.262; // calculated with linear regression of the best values for specific viewportMins
      responsiveRadius = this.type === ChartType.Doughnut ? [(radius / this.doughnutInnerRadiusScaleFactor) + '%', radius + '%'] : radius + '%';
      if (this.showLabels === false) {
        responsiveRadius = this.type === ChartType.Doughnut ? ['60%', '80%'] : radius + '%';
      }
    }

    const responsiveOptions: any = {
      series: [{
        radius: this.radius || responsiveRadius,
        label: {
          show: this.showLabels || responsiveLabelVisibility,
          fontSize: responsiveFontSize
        },
        minAngle: 5,
        padAngle: 2,
      }],
      tooltip: {
        textStyle: {
          fontSize: responsiveFontSize * this.tooltipFontScaleFactor
        }
      }
    };

    if (this.showLegend && responsiveShowLegend) {
      responsiveOptions.series[0].center = ['25%', '50%'];
    } else {
      responsiveOptions.series[0].center = 'center';
    }

    responsiveOptions.legend = {
      show: this.showLegend && responsiveShowLegend,
      orient: 'vertical',
      right: 10,
      top: 'middle',
      itemWidth: 4,
      itemHeight: viewportMin / 9.25,
      selectedMode: false, // Make legend non-clickable
      data: this.data.map(item => item.name),
      formatter: (name) => {
        const item = this.data.find(d => d.name === name);
        if (item) {
          return `{name|${name}}  {value|${item.value}}`;
        }
        return name;
      },
      textStyle: {
        rich: {
          name: {
            fontSize: responsiveFontSize * this.tooltipFontScaleFactor,
            color: '#fff',
            width: viewportMin / 1.25,
            align: 'left'
          },
          value: {
            fontSize: responsiveFontSize * this.tooltipFontScaleFactor,
            color: '#fff',
            align: 'right'
          }
        }
      },
    };

    if (this.showGraphic) {
      responsiveOptions.graphic = {
          type: 'text',
          left: responsiveGraphicLeftPosition,
          top: 'center',
          style: {
            text: `{b|${this.total}}\n` + this.translateService.instant('Total'), // Replace with your dynamic text
            textAlign: 'center',
            rich: {
              b: {
                fontSize: responsiveFontSize * this.tooltipFontScaleFactor * 2,
                fontWeight: 'bold',
                color: '#fff'
              }
            },
            fontSize: responsiveFontSize * this.tooltipFontScaleFactor,
            fill: 'white'
          }
      };
    }

    return responsiveOptions;
  }

  /**
   * A label formatter method to format pie chart labels
   * @param params
   */
  labelFormatter(params) {
    let label = '';

    if (this.chartSettings.seriesSettings.length > 0) {
      const chartSeries = this.chartSettings.seriesSettings[0];
      const kpiSeries = this.kpi.series[chartSeries.index];

      // set value
      const value = DataFormatUtil.formatValue(params.value, kpiSeries.format, chartSeries.precision, chartSeries.thousandSeparator);

      // set label
      label += `${params.name}\n(${value})`;
    }

    return label;
  }

  calculateTotal(data: any[]) {
    this.total = data.reduce((acc, item) => acc + item.value, 0);
  }

  setData(data: NGSIResult[]) {
    [this.rawData, this.colors] = this.dataExtractionUtil.extractData(this.kpi, this.chartSettings, data);

    // TODO: handle multidimensional/multiseries charts
    let dimensionData, seriesData;
    this.rawData.forEach(axis => {
      if (axis.dimension) {
        dimensionData = axis;
      } else if (axis.series) {
        seriesData = axis;
      }
    });

    this.seriesName = seriesData.series.label;
    this.data = seriesData.data.map((value, index) => {
      return {
        name: dimensionData.data[index],
        value: value
      };
    });

    this.calculateTotal(this.data);
    if (this.showGraphic) {
      this.setGraphicText(`{b|${this.total}}\n` + this.translateService.instant('Total'));
    }
    this.setSeriesData(this.data);
  }
}
